import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Upload, ArrowLeft, AlertTriangle, X, CheckCircle, XCircle, Baby,
} from 'lucide-react'
import { importPregnancyCampaign, getPregnancyCampaigns } from '../../services/api'

// Server-side import: the vet's CSV/XLSX goes up as-is and the backend
// parses, matches by RFID, records pregnancy_check events, and updates each
// animal's pregnancy flag + expected calving date. This page is just the
// envelope (campaign details + file) and the per-row result table.

const STATUS_STYLES = {
  recorded_pregnant: { label: 'Pregnant', cls: 'bg-green-100 text-green-700' },
  recorded_open: { label: 'Open', cls: 'bg-blue-100 text-blue-700' },
  recorded_unknown: { label: 'Unknown', cls: 'bg-gray-100 text-gray-600' },
  unmatched: { label: 'No match', cls: 'bg-amber-100 text-amber-700' },
  duplicate: { label: 'Duplicate', cls: 'bg-gray-100 text-gray-500' },
}

export default function PregnancyCampaignImport({ ranchId }) {
  const navigate = useNavigate()
  const [error, setError] = useState('')
  const [file, setFile] = useState(null)
  const [testDate, setTestDate] = useState(new Date().toISOString().split('T')[0])
  const [vetName, setVetName] = useState('')
  const [vetPractice, setVetPractice] = useState('')
  const [notes, setNotes] = useState('')
  const [result, setResult] = useState(null)

  const { data: campaigns } = useQuery({
    queryKey: ['pregnancy-campaigns', ranchId],
    enabled: !!ranchId,
    queryFn: () => getPregnancyCampaigns(ranchId),
  })

  const importMutation = useMutation({
    mutationFn: importPregnancyCampaign,
    onSuccess: (data) => setResult(data),
    onError: (err) => setError(err.response?.data?.detail || 'Import failed'),
  })

  const handleSubmit = () => {
    if (!file) { setError('Choose the vet’s results file first'); return }
    setError('')
    const formData = new FormData()
    formData.append('ranch_id', ranchId)
    formData.append('test_date', testDate)
    if (vetName) formData.append('vet_name', vetName)
    if (vetPractice) formData.append('vet_practice', vetPractice)
    if (notes) formData.append('notes', notes)
    formData.append('file', file)
    importMutation.mutate(formData)
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/cattle')} className="p-2 rounded-lg hover:bg-gray-100">
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Import Pregnancy-Test Campaign</h1>
          <p className="text-gray-600">Upload the vet&apos;s results file (CSV or Excel: RFID, result, optional days/months pregnant)</p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {!result && (
        <div className="bg-white rounded-lg shadow p-6 max-w-2xl mx-auto space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Results File</label>
            <label className="flex flex-col items-center justify-center w-full h-36 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors">
              <Upload className="h-10 w-10 text-gray-400 mb-2" />
              <span className="text-sm text-gray-600">{file ? file.name : 'Click to upload CSV or XLSX'}</span>
              <span className="text-xs text-gray-400 mt-1">Columns: RFID/EID, Result (pregnant/open), Days or Months pregnant (optional)</span>
              <input
                type="file"
                accept=".csv,.xlsx,.xlsm"
                onChange={(e) => setFile(e.target.files[0] || null)}
                className="hidden"
              />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Test Date</label>
              <input
                type="date" value={testDate} onChange={(e) => setTestDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vet Name</label>
              <input
                type="text" value={vetName} onChange={(e) => setVetName(e.target.value)} placeholder="Optional"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Practice</label>
              <input
                type="text" value={vetPractice} onChange={(e) => setVetPractice(e.target.value)} placeholder="Optional"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <input
                type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          <button
            onClick={handleSubmit}
            disabled={importMutation.isPending || !file}
            className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
          >
            {importMutation.isPending ? (
              <>
                <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                Importing...
              </>
            ) : (
              <>
                <Baby className="h-5 w-5" />
                Import Campaign
              </>
            )}
          </button>

          {campaigns?.items?.length > 0 && (
            <div className="border-t pt-4">
              <h2 className="text-sm font-medium text-gray-700 mb-2">Recent campaigns</h2>
              <div className="space-y-1">
                {campaigns.items.slice(0, 5).map((c) => (
                  <div key={c.id} className="flex justify-between text-sm text-gray-600">
                    <span>{c.test_date} {c.vet_name ? `— ${c.vet_name}` : ''}</span>
                    <span>
                      {c.pregnant_count} pregnant / {c.open_count} open
                      {c.unmatched_count > 0 && (
                        <span className="text-amber-600"> / {c.unmatched_count} unmatched</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {result && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Rows</div>
              <div className="text-2xl font-bold">{result.total_rows}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pregnant</div>
              <div className="text-2xl font-bold text-green-600">{result.pregnant}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Open</div>
              <div className="text-2xl font-bold text-blue-600">{result.open}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Unknown</div>
              <div className="text-2xl font-bold text-gray-500">{result.unknown}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Unmatched</div>
              <div className="text-2xl font-bold text-amber-600">{result.unmatched}</div>
            </div>
          </div>

          {result.unmatched > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700">
              <AlertTriangle className="inline h-5 w-5 mr-2" />
              {result.unmatched} tag(s) matched no animal in the system. The vet handled these
              animals, so they may be unregistered or retagged — a review item has been raised
              on the assurance ledger.
            </div>
          )}

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">RFID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Animal</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expected Calving</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {result.results.map((r, i) => {
                    const st = STATUS_STYLES[r.status] || { label: r.status, cls: 'bg-gray-100 text-gray-600' }
                    return (
                      <tr key={i} className={r.status === 'unmatched' ? 'bg-amber-50' : ''}>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${st.cls}`}>
                            {st.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-mono">{r.rfid}</td>
                        <td className="px-4 py-3 text-sm font-medium">
                          {r.animal || (
                            <span className="text-amber-600 flex items-center gap-1">
                              <XCircle className="h-4 w-4" /> —
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">{r.expected_calving_date || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{r.message}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-center gap-3">
            <button
              onClick={() => { setResult(null); setFile(null) }}
              className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
            >
              Import Another File
            </button>
            <button
              onClick={() => navigate('/cattle')}
              className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
            >
              <CheckCircle className="h-5 w-5" />
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
